import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { isAbortError, requestIdleCallback } from '@theme/utilities';

/** @type {Record<string, string>} */
const sharedRecommendationsCache = {};

/** @type {Map<string, Promise<{ success: true, data: string } | { success: false, status: number }>>} */
const recommendationsInFlightRequests = new Map();

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#scheduleLoadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#scheduleLoadRecommendations();
      break;
    }
  });

  /** @type {number | null} */
  #idleCallbackId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#cancelScheduledLoad();
  }

  /**
   * Defer loading until the browser is idle to avoid competing with critical work.
   */
  #scheduleLoadRecommendations() {
    this.#cancelScheduledLoad();

    if ('requestIdleCallback' in window) {
      this.#idleCallbackId = window.requestIdleCallback(() => {
        this.#idleCallbackId = null;
        this.#loadRecommendations();
      });
      return;
    }

    this.#idleCallbackId = /** @type {number} */ (
      requestIdleCallback(() => {
        this.#idleCallbackId = null;
        this.#loadRecommendations();
      })
    );
  }

  #cancelScheduledLoad() {
    if (this.#idleCallbackId === null) return;

    if ('cancelIdleCallback' in window) {
      window.cancelIdleCallback(this.#idleCallbackId);
    } else {
      clearTimeout(this.#idleCallbackId);
    }

    this.#idleCallbackId = null;
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const fragment = new DOMParser().parseFromString(result.data || '', 'text/html');
        const recommendations = fragment.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations instanceof HTMLElement && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.classList.remove('hidden');
          delete this.dataset.error;
          morph(this, recommendations, MORPH_OPTIONS);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and caches the result for future use across all instances.
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = sharedRecommendationsCache[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    let inFlightRequest = recommendationsInFlightRequests.get(url);
    if (!inFlightRequest) {
      inFlightRequest = (async () => {
        const response = await fetch(url);
        if (!response.ok) {
          return { success: false, status: response.status };
        }

        const text = await response.text();
        sharedRecommendationsCache[url] = text;
        return { success: true, data: text };
      })();
      recommendationsInFlightRequests.set(url, inFlightRequest);
    }

    try {
      return await inFlightRequest;
    } finally {
      if (recommendationsInFlightRequests.get(url) === inFlightRequest) {
        recommendationsInFlightRequests.delete(url);
      }
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
